import type { AxiosError } from 'axios';
import { GraphQLError } from 'graphql';
import { JSONObject } from 'zapatos/db';
import { AtomicError } from './atomic/errors';
import { PlaidError } from './plaid/errors';
import { isObject } from './util/types';

/** Standard format for logging HTTP details in errors */
export interface ErrorDetailsHttp {
    method?: string;
    status_code?: number;
    url?: string;
}

/** Standard format for serializing custom error classes to JSON */
export interface ErrorJSON {
    name: string;
    message: string;
    stack?: string;
    details?: Record<string, unknown>;
    extensions?: Record<string, unknown>;
}

export function getGraphQLErrorMessageAndExtensions(err: unknown) {
    if (err instanceof AtomicError) {
        return { message: 'AtomicError' as const, extensions: err.extensions };
    }
    if (err instanceof PlaidError) {
        return { message: 'PlaidError' as const, extensions: err.extensions };
    }
    if (err instanceof CustomError) {
        return { message: 'CustomError' as const, extensions: err.extensions };
    }
    return { message: 'Error' as const, extensions: {} };
}

export function getResponseGraphQLError(error: GraphQLError) {
    return {
        ...getGraphQLErrorMessageAndExtensions(error.originalError),
        locations: error.locations,
        path: error.path,
    };
}

export type ResponseGraphQLError = Readonly<
    ReturnType<typeof getResponseGraphQLError>
>;

/**
 * Generic custom error class, allows additional properties and includes them
 * when the Error instance is serialized with JSON.stringify, e.g. when logging.
 */
export class CustomError extends Error {
    details?: CustomErrorDetails;
    extensions?: CustomErrorExtensions;

    constructor(
        message: string,
        /** never returned to the client in production */
        details?: CustomErrorDetails,
        /** returned to the client in production via the GraphQL `extensions` property */
        extensions?: CustomErrorExtensions
    ) {
        super(message);
        this.name = this.constructor.name;
        this.details = details;
        this.extensions = extensions;
        // TypeScript requires a workaround when extending built-ins like Error
        // Ref: https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, CustomError.prototype);
    }

    toJSON(): ErrorJSON {
        return {
            name: this.name,
            message: this.message,
            stack: this.stack,
            details: this.details,
            extensions: this.extensions,
        };
    }
}

interface CustomErrorDetails extends Record<string, unknown> {
    http?: ErrorDetailsHttp;
}

interface CustomErrorExtensions extends Record<string, unknown> {
    code: CustomErrorExtensionsCode;
}

export type CustomErrorExtensionsCode =
    | 'ATOMIC_DISABLED'
    | 'AUTH_MISSING'
    | 'AUTH_REJECTED'
    | 'CREATE_USER_EMAIL_AMBIGUOUS'
    | 'CREATE_USER_EMAIL_MISSING'
    | 'DELETE_GOAL_ALREADY_DELETED'
    | 'DELETE_GOAL_PENDING_TRANSACTIONS'
    | 'DELETE_GOAL_PLENTY_IRA_EXISTS'
    | 'DELETE_FE_ALREADY_DELETED'
    | 'DELETE_FE_PENDING_TRANSACTIONS'
    | 'DELETE_GOAL_CAN_LEAVE_SPLIT_GOAL_HAS_BALANCE'
    | 'DELETE_GOAL_CAN_LEAVE_SPLIT_GOAL_ZERO_BALANCE'
    | 'INVALID_EMAIL'
    | 'UNDER_REVIEW'
    | 'USER_MERCHANT_FOR_GLOBAL_RULE'
    | 'PARTNERSHIP_INVITE_EXPIRED'
    | 'PARTNERSHIP_INVITE_EXISTING_INVITATION'
    | 'INVALID_SPLIT_AMOUNTS'
    | 'SIGNUPS_DISABLED';

/**
 * This is equivalent to the helper in the `axios` package
 * Ref: https://github.com/axios/axios/blob/a52e4d9af51205959ef924f87bcf90c605e08a1e/lib/helpers/isAxiosError.js
 */
export function isAxiosError(err: unknown): err is AxiosError {
    return isObject(err) && 'isAxiosError' in err && err.isAxiosError === true;
}

export function assert(condition: unknown, msg?: string): asserts condition {
    if (condition) return;

    throw new Error(msg ? `Assertion failed: ${msg}` : `Assertion failed`);
}

/**
 * Returns a simplified error object with the stack trace removed,
 * to be used when storing errors in DB columns.
 */
export const getSimplifiedErrorObject = (err: unknown): JSONObject => {
    if (err instanceof Error) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { stack, cause, ...errWithoutStackTrace } = err;
        return errWithoutStackTrace;
    }
    return {};
};
